import React, { Component } from 'react';
import { MutationFunction } from 'react-apollo';
import { compose } from 'recompose';
import { withRouter, RouteComponentProps } from 'react-router';
import { Form as FormikForm, Formik, FormikProps } from 'formik';

import {
  CreateGroupComponent,
  CreateGroupMutation,
  GroupColor,
  GroupContentApprovalType,
  GroupIcon,
  GroupIconColor,
} from 'api';
import { Dimensions, Layouts } from 'components';
import { withUser, withNotify, IWithNotify, IWithUser } from 'providers';
import { withGroupColors, IWithGroupColors } from 'providers/queries';
import { getGroupHexColor } from 'features/groups/helpers';

import { Actions, Header } from '../components/New';
import { FormLeft, FormRight } from '../components/FormShared';
import { IGroupForm, IMatchParams } from '../types';
import { GroupSchema } from '../validations';

import Controller from '../Controller';
import { groupCreate } from 'apollo/reducers';

interface IPropsEnhanced
  extends IWithUser,
    IWithGroupColors,
    IWithNotify,
    RouteComponentProps<IMatchParams> {}

interface IState {
  isWorking: boolean;
}

const enhancers = compose<IPropsEnhanced, {}>(withUser, withNotify, withGroupColors, withRouter);

class NewGroup extends Component<IPropsEnhanced, IState> {
  state: IState = {
    isWorking: false,
  };

  getInitialValues(): IGroupForm {
    return {
      title: '',
      content_approval_type: GroupContentApprovalType.Auto,
      allow_user_posts: true,
      read_only: false,
      color: GroupColor.Magenta,
      icon_color: GroupIconColor.Magenta,
      icon: GroupIcon.Es,
      location_id: null,
      department_id: null,
      is_private: false,
    };
  }

  getDividerColor(formikProps: FormikProps<IGroupForm>) {
    const { groupColors } = this.props;
    const { color } = formikProps.values;

    return getGroupHexColor(groupColors, color);
  }

  handleCompleted = (data: CreateGroupMutation) => {
    const { history } = this.props;
    const { createGroup } = data;

    this.setState({ isWorking: false }, () => {
      history.push(`/group/${createGroup.id}`);
    });
  };

  handleError = () => {
    const { notify } = this.props;
    this.setState({ isWorking: false });
    notify.mutationError();
  };

  handleSubmit = (mutationFn: MutationFunction) => (values: IGroupForm) => {
    const { user } = this.props;

    this.setState({ isWorking: true });

    return mutationFn({
      variables: { data: values },
      update: (cache, result) => {
        groupCreate(cache, result, { currentUser: user });
      },
    });
  };

  renderMainRight(formikProps: FormikProps<IGroupForm>) {
    const { groupColors } = this.props;
    const { isWorking } = this.state;

    const { color } = formikProps.values;

    return (
      <Dimensions>
        {({ width }) => (
          <div style={{ width }}>
            <FormRight
              width={width}
              hexColor={getGroupHexColor(groupColors, color)}
              formikProps={formikProps}
            />
            <Actions
              isWorking={isWorking}
              hexColor={getGroupHexColor(groupColors, color)}
              formikProps={formikProps}
            />
          </div>
        )}
      </Dimensions>
    );
  }

  render() {
    const initialValues = this.getInitialValues();

    return (
      <Controller>
        <CreateGroupComponent onError={this.handleError} onCompleted={this.handleCompleted}>
          {mutationFn => (
            <Formik
              onSubmit={this.handleSubmit(mutationFn)}
              initialValues={initialValues}
              validationSchema={GroupSchema()}>
              {(formikProps: FormikProps<IGroupForm>) => (
                <FormikForm>
                  <Layouts.App1Column
                    desktopSize={12}
                    main={
                      <Layouts.AppMain2Column
                        dividerColor={this.getDividerColor(formikProps)}
                        header={<Header />}
                        left={<FormLeft formikProps={formikProps} type="create" />}
                        right={this.renderMainRight(formikProps)}
                      />
                    }
                  />
                </FormikForm>
              )}
            </Formik>
          )}
        </CreateGroupComponent>
      </Controller>
    );
  }
}

export default enhancers(NewGroup);
